import "./assets/main.css";
// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "jsvectormap/dist/jsvectormap.css";
import "flatpickr/dist/flatpickr.css";
import "simplebar-vue/dist/simplebar.min.css";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { isAxiosError } from "axios";
import { createPinia } from "pinia";
import { createApp } from "vue";

// Analytics
import {
  initMixpanel,
  identifyUser,
  startSessionRecording,
  setCurrentUserImpersonating,
  syncFeatureFlagFirm,
} from "@/services/analytics";
import { initSentry, setSentryUser } from "@/services/monitoring";
import { useAuthStore } from "@/stores/auth";

import App from "./App.vue";
import { appUpdateRequired } from "./composables/useAppUpdate";
import router from "./router";

function showAppUpdateModal() {
  appUpdateRequired.value = true;
}

// Detect stale chunk errors across browsers (Chrome, Safari, Firefox)
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("dynamically imported module") ||
    msg.includes("module script failed") ||
    msg.includes("loading module")
  );
}

// Handle stale chunk errors after deployments
window.addEventListener("vite:preloadError", () => {
  showAppUpdateModal();
});

// Catch chunk errors from dynamic imports outside of router navigation
window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault();
    showAppUpdateModal();
  }
});

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);
// Error tracking + tracing. No-op unless VITE_SENTRY_ENABLED + DSN are set.
initSentry(app, router);

// Catch chunk load failures during navigation (stale deploy)
router.onError((error) => {
  if (isChunkLoadError(error)) {
    showAppUpdateModal();
  }
});
/**
 * Skip retries on 4xx responses — auth/permission/validation failures are
 * deterministic and 3x retries just delay the error UI. Network and 5xx
 * blips still get the default 3 retries. Applied as the QueryClient default
 * so every useQuery inherits this policy; individual queries can still
 * override `retry` if they need different behavior.
 */
const retryUnlessClientError = (failureCount: number, error: Error) => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status && status >= 400 && status < 500) return false;
  }
  return failureCount < 3;
};

app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: retryUnlessClientError,
        // Sets the default staleTime for all useQuery calls. Individual queries can still override it.
        staleTime: 1000 * 60 * 5, // 5 minutes
      },
    },
  },
});

// Initialize analytics (before auth so SDK is ready)
initMixpanel();

const authStore = useAuthStore();

// Resolve auth, then mount. Server-state data (dashboard/campaigns/firm-stats)
// is fetched lazily by each view via vue-query — no eager prefetch needed.
authStore
  .initAuth()
  .then(() => {
    if (authStore.isAuthenticated) {
      setCurrentUserImpersonating(authStore.isImpersonating);
      const user = authStore.getUser;
      if (user) {
        syncFeatureFlagFirm(user);
        identifyUser(user.id, user.email);
        setSentryUser({
          impersonating: authStore.isImpersonating,
          organizationId: user.organizationId ?? user.organization?.id,
          orgType: user.organization?.orgType,
          role: user.roles?.[0]?.name,
          userId: user.id,
        });
        startSessionRecording();
      }
    }
    app.mount("#app");
  })
  .catch(() => {
    app.mount("#app"); // Mount the app anyway so error UI can render
  });
